import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { extractError } from '@/api/client';
import { toast } from '@/stores/toastStore';
import { documentsRhApi } from '@/api/documentsRh';

/**
 * Lot CONTRATS (20/10/2026) — engagement de travailleur journalier (modèle 5 validé par le cabinet). Le taux journalier
 * vient du registre (jamais retapé) ; à défaut, le contrat renvoie au barème à la pièce affiché.
 */
const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="block space-y-1"><span className="text-xs font-medium text-gray-700">{label}</span>{children}</label>
);

export const EngagementJournalierDialog = ({ open, onClose, ouvrier }: {
  open: boolean; onClose: () => void; ouvrier: { id: string; nom: string; telephone: string; metier: string | null };
}) => {
  const qc = useQueryClient();
  const cle = ['hr', 'documents', 'ouvrier', ouvrier.id];
  const docs = useQuery({ queryKey: cle, queryFn: () => documentsRhApi.listerOuvrier(ouvrier.id), enabled: open });
  const [f, setF] = useState({ dateDebut: '', tache: '', lieu: '', equipe: '', jourPaiement: '' });
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    if (open) { setF({ dateDebut: new Date().toISOString().slice(0, 10), tache: '', lieu: '', equipe: '', jourPaiement: '' }); setErreur(null); }
  }, [open]);
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const telecharger = (d: { id: string; numero: string }) =>
    documentsRhApi.telecharger(d.id, d.numero).catch(() => toast.error('PDF indisponible', 'Réessayez dans un instant.'));

  const envoyer = async (e: FormEvent) => {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const d = await documentsRhApi.emettreEngagement({
        ouvrierId: ouvrier.id, dateDebut: f.dateDebut, tache: f.tache, lieu: f.lieu, equipe: f.equipe.trim() || undefined, jourPaiement: f.jourPaiement,
      });
      toast.success('Engagement émis', d.numero);
      void qc.invalidateQueries({ queryKey: cle });
      void telecharger(d);
      onClose();
    } catch (err) {
      setErreur(extractError(err).message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Engagement de travailleur journalier" subtitle={`${ouvrier.nom} — ${ouvrier.telephone}`}>
      <form onSubmit={envoyer} className="space-y-3">
        {erreur && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{erreur}</div>}
        <p className="text-xs text-gray-500">
          Fiche courte à faire signer (ou marquer d’une empreinte) à l’engagement. Le taux journalier est celui du registre.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date de début *"><Input type="date" value={f.dateDebut} onChange={(e) => set('dateDebut', e.target.value)} required /></Field>
          <Field label="Jour de paiement *"><Input value={f.jourPaiement} onChange={(e) => set('jourPaiement', e.target.value)} placeholder="samedi" required /></Field>
        </div>
        <Field label="Tâche confiée *"><Input value={f.tache} onChange={(e) => set('tache', e.target.value)} placeholder="la fabrication de poteaux" required /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Lieu (site) *"><Input value={f.lieu} onChange={(e) => set('lieu', e.target.value)} required /></Field>
          <Field label="Équipe"><Input value={f.equipe} onChange={(e) => set('equipe', e.target.value)} /></Field>
        </div>
        {(docs.data?.length ?? 0) > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-medium text-gray-700">Engagements déjà émis</div>
            {docs.data!.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-xs" style={{ opacity: d.annuleLe ? 0.55 : 1 }}>
                <span>{d.numero} · {new Date(d.emisLe).toLocaleDateString('fr-FR')}{d.annuleLe ? ` · annulé : ${d.motifAnnulation}` : ''}</span>
                <button type="button" className="ds-btn ds-btn-g ds-btn-sm" aria-label={`Télécharger ${d.numero}`} onClick={() => void telecharger(d)}><Download size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Fermer</Button>
          <Button type="submit" disabled={envoi}>{envoi ? 'Émission…' : 'Émettre l’engagement'}</Button>
        </div>
      </form>
    </Dialog>
  );
};
